import * as React from "react"
import 'intersection-observer';
import { InView } from 'react-intersection-observer';
import { Action, IOWidget, mk_widget, Pair } from "widgets-for-react"
import { CMSState, Id, ImageSize, Url } from "../../../../cms/blocks/types.gen";
import { blockInviewHandler } from "../../../../drupal/rendering/inview_utils.mod";
import { getClassName, link } from "../../../../cms/blocks/utils.gen";
import { ImageContainer } from "../../../../cms/blocks/widgets/ImageContainer.gen";
import { parseImageUrl } from "../../../../drupal/rendering/image_utils.mod";
import { ParsedMediaImage } from "../../../../drupal/types_and_parser/types";

export type text_color = "black" | "white"

export type ctaBlockProps<extra_images> = {
  index: number
  title?: React.ReactNode
  intro: string
  display_option: string
  field_image?: ParsedMediaImage
  image?: Id
  image_alt?: string
  image_size?: ImageSize<extra_images>
  text_color: text_color
  get_default_data_src: (() => ImageSize<extra_images>)
  get_default_data_srcset: (() => Pair<ImageSize<extra_images>, string>[])
  buttons: "none" | "primary" | "primary_secondary"
  link_type: boolean
  primary_button?: ({
    kind: "link"
    is_link_external: boolean
    target: "_blank" | "_self"
    url: Url
  }) & { title: string }
  secondary_button?: ({
    kind: "link"
    is_link_external: boolean
    target: "_blank" | "_self"
    url: Url
  }) & { title: string }
  inview?: {
    threshold?: number
    triggerOnce?: boolean
  }
}

export type ctaBlockStyle = Partial<{
  block_contentwidth_classname: string
  block_fullwidth_classname: string
  inview_classname: string
  inner_classname: string
  header_classname: string
  title_classname: string
  content_classname: string
  text_classname: string
  footer_classname: string
  visual_classname: string
  image_classname: string
  clickable_wrapper_classname: string
  clickable_primary_classname: string
  clickable_secondary_classname: string
}>

export function ctaBlock<customBlockData, extra_images>(
  props: ctaBlockProps<extra_images>,
  options?: { style?: ctaBlockStyle; key?: string }
): IOWidget<CMSState<customBlockData, extra_images, never>, Action<CMSState<customBlockData, extra_images, never>>> {
  return (cmsState: CMSState<customBlockData, extra_images, never>) =>
    mk_widget({
      run: _ => (
        <InView
          as="section"
          onChange={(inView) => blockInviewHandler('cta', inView, `io-${options && options.key ? options.key : props.index}`, getClassName(options, "inview_classname", "inview"))}
          triggerOnce={props.inview && props.inview.triggerOnce ? props.inview.triggerOnce : false}
          threshold={props.inview && props.inview.threshold ? props.inview.threshold : 0.1}
          id={`io-${options && options.key ? options.key : props.index}`}
          className={getClassName(options, props.display_option === 'content-width' ? "block_contentwidth_classname" : "block_fullwidth_classname", props.display_option === 'content-width' ? "container py-5" : "container-fluid bg-light my-5")}
          key={options && options.key ? options.key : `block-${props.index}`}
        >
          <div className={getClassName(options, "inner_classname", `container d-flex ${props.display_option === 'content-width' ? "bg-light" : ""}`)}>
            <div className={getClassName(options, "content_classname", "p-5")}>
              {props.title && (
                // Green = white title & text | Orange == black title & text
                <header className={getClassName(options, "header_classname", "")}>
                  <h2 className={getClassName(options, "title_classname", "text-center mb-3") + (props.text_color == 'black' ? ' block__title--dark' : '')}>{props.title}</h2>
                </header>
              )}

              <p className={getClassName(options, "text_classname", "lead text-center") + (props.text_color == 'black' ? ' block__text--dark' : '')}>{props.intro}</p>

              {(props.primary_button || props.secondary_button) && (
                <footer className={getClassName(options, "footer_classname", "text-center")}>
                  {props.primary_button && props.primary_button.title && props.primary_button.url
                    ? <div className={getClassName(options, "clickable_wrapper_classname", "")}>
                      {link(props.primary_button,
                        {
                          classname:
                            props.link_type === false
                              ? getClassName(options, "clickable_primary_classname", "btn btn-dark mr-2")
                              : getClassName(options, "clickable_secondary_classname", "btn btn-link px-0")
                        },
                        props.primary_button.title,
                        () => {
                          window.dataLayer.push({
                            event: 'event.refresco',
                            category: 'CTABlock',
                            action: props.primary_button.title,
                            label: window.location.pathname,
                          })
                        }
                      )}
                    </div>
                    : null}
                  {props.secondary_button && props.secondary_button.title && props.secondary_button.url
                    ? <div className={getClassName(options, "clickable_wrapper_classname", "")}>
                      {link(props.secondary_button,
                        { classname: getClassName(options, "clickable_secondary_classname", "btn btn-link px-0") },
                        props.secondary_button.title,
                        () => {
                          window.dataLayer.push({
                            event: 'event.refresco',
                            category: 'CTABlock',
                            action: props.primary_button.title,
                            label: window.location.pathname,
                          })
                        }
                      )}
                    </div>
                    : null}
                </footer>
              )}
            </div>

            {props.image &&
              <div className={getClassName(options, "visual_classname", "w-100")}>
                <img className={getClassName(options, "image_classname", `w-100 ${props.display_option !== 'content-width' ? "d-block ml-auto" : ""}`)}
                  src="/frontend/images/empty.png"
                  data-src={`${props.field_image?.field_media_image?.image?.style_urls['1400w']}`}
                  data-srcset={`${parseImageUrl(
                    props.field_image?.field_media_image?.image?.style_urls['700w']
                  )} 700w, ${parseImageUrl(
                    props.field_image?.field_media_image?.image?.style_urls['1400w']
                  )} 1400w, ${parseImageUrl(
                    props.field_image?.field_media_image?.image?.style_urls['2800w']
                  )} 2800w`}
                  alt={props.image_alt}
                  sizes="auto"
                  width="1400"
                  height="300"
                />
              </div>
            }
          </div>
        </InView>
      ),
    })
}
